import { InputAdornment, Table, TableBody, TableCell, TableRow } from '@mui/material';
import { useFormContext } from 'react-hook-form';

import type { WelcomeBonusGameWeightingCategory, WelcomeBonusGameWeightingContributions } from '../../../../@types/api';
import { WELCOME_BONUS_GAME_WEIGHTING_OPTIONS } from '../../../../constants';
import { WELCOME_BONUS_GAME_WEIGHTING_CATEGORY } from '../../../../constants/enums';
import type { WageringFieldsFormData } from '../../../../schema';
import FormNumberInput from '../../../molecules/FormNumberInput';

const styles = {
  gameWeightingTable: {
    '& .MuiTableCell-root': {
      pl: 1.5,
      pr: 0,
      py: 1,
    },
  },
} as const;

export const gameWeightingPayloadKeyMap = {
  [WELCOME_BONUS_GAME_WEIGHTING_CATEGORY.SLOTS]: 'slots',
  [WELCOME_BONUS_GAME_WEIGHTING_CATEGORY.ROULETTE]: 'roulette',
  [WELCOME_BONUS_GAME_WEIGHTING_CATEGORY.BLACKJACK]: 'blackjack',
  [WELCOME_BONUS_GAME_WEIGHTING_CATEGORY.LIVE_CASINO]: 'liveCasino',
} as const satisfies Record<WelcomeBonusGameWeightingCategory, keyof WelcomeBonusGameWeightingContributions>;

export const createGameWeightingContributionRows = (
  contributionPercentage: number | null
): WageringFieldsFormData['gameWeightingContributions'] =>
  WELCOME_BONUS_GAME_WEIGHTING_OPTIONS.map((option) => ({
    category: option.id,
    contributionPercentage,
  }));

export const toGameWeightingContributionsPayload = (
  contributions: WageringFieldsFormData['gameWeightingContributions']
): WelcomeBonusGameWeightingContributions =>
  contributions.reduce<WelcomeBonusGameWeightingContributions>((payload, contribution) => {
    if (contribution.contributionPercentage == null) return payload;

    return {
      ...payload,
      [gameWeightingPayloadKeyMap[contribution.category]]: contribution.contributionPercentage,
    };
  }, {});

export const toCompleteGameWeightingContributionsPayload = (
  contributions: WageringFieldsFormData['gameWeightingContributions']
): WelcomeBonusGameWeightingContributions =>
  contributions.reduce<WelcomeBonusGameWeightingContributions>(
    (payload, contribution) => ({
      ...payload,
      [gameWeightingPayloadKeyMap[contribution.category]]: contribution.contributionPercentage,
    }),
    {}
  );

export const toGameWeightingContributionRows = (
  contributions: WelcomeBonusGameWeightingContributions
): WageringFieldsFormData['gameWeightingContributions'] =>
  WELCOME_BONUS_GAME_WEIGHTING_OPTIONS.map((option) => ({
    category: option.id,
    contributionPercentage: contributions[gameWeightingPayloadKeyMap[option.id]] ?? null,
  }));

type WageringFieldsVariantProps = {
  required: boolean;
};

const SportsbookWageringFieldInputs = ({ required }: WageringFieldsVariantProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<WageringFieldsFormData>();

  return (
    <>
      <FormNumberInput
        control={control}
        name='minSelections'
        label='Min. Selections number'
        error={errors.minSelections}
        allowDecimals={false}
        required={required}
      />
      <FormNumberInput
        control={control}
        name='minOddsPerSelection'
        label='Min. Odds of each selection'
        error={errors.minOddsPerSelection}
        allowDecimals
        slotProps={{ htmlInput: { inputMode: 'decimal' } }}
        valueAsString
        required={required}
      />
    </>
  );
};

export const SportsbookWageringFields = () => <SportsbookWageringFieldInputs required={false} />;

export const RequiredSportsbookWageringFields = () => <SportsbookWageringFieldInputs required />;

const CasinoWageringFieldInputs = ({ required }: WageringFieldsVariantProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<WageringFieldsFormData>();

  return (
    <>
      <FormNumberInput
        control={control}
        name='maxBetLimit'
        label='Max Bet Limit'
        error={errors.maxBetLimit}
        allowDecimals
        displayCurrency
        required={required}
      />
      <Table size='small' sx={styles.gameWeightingTable} aria-label='Game weighting contributions'>
        <TableBody>
          {WELCOME_BONUS_GAME_WEIGHTING_OPTIONS.map((option, index) => (
            <TableRow key={option.id}>
              <TableCell>{option.name}</TableCell>
              <TableCell>
                <FormNumberInput
                  control={control}
                  name={`gameWeightingContributions.${index}.contributionPercentage`}
                  label='Contribution'
                  error={errors.gameWeightingContributions?.[index]?.contributionPercentage}
                  allowDecimals={false}
                  fullWidth
                  required={required}
                  slotProps={{ input: { endAdornment: <InputAdornment position='end'>%</InputAdornment> } }}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
};

export const CasinoWageringFields = () => <CasinoWageringFieldInputs required={false} />;

export const RequiredCasinoWageringFields = () => <CasinoWageringFieldInputs required />;
